import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';
import { api } from '../utils/api';

export const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.plans.getAll();
        setPlans(res.data);
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-white/60">Cargando...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen grain pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl sm:text-6xl font-bold mb-6 tracking-tight" data-testid="plans-page-title">
            Planes y <span className="text-gradient">Servicios</span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Elige el plan perfecto para tu proyecto. Precios transparentes y sin sorpresas.
          </p>
        </motion.div>
        
        {plans.length === 0 ? (
          <div className="text-center text-white/60 py-12">
            No hay planes disponibles
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative glass rounded-3xl p-8 ${
                  plan.recommended
                    ? 'border-[#C6FF00]/50 shadow-[0_0_40px_rgba(198,255,0,0.2)]'
                    : 'hover:border-white/20'
                } transition-all duration-300`}
                data-testid={`plan-card-${index}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="px-4 py-1 rounded-full bg-[#C6FF00] text-black text-[10px] font-black uppercase flex items-center space-x-1 tracking-widest">
                      <Zap className="w-3 h-3" />
                      <span>Recomendado</span>
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                  <div className="flex items-baseline justify-center space-x-2">
                    <span className="text-5xl font-black" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${plan.price}
                    </span>
                    <span className="text-white/60 text-lg">/{plan.billing_period}</span>
                  </div>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        plan.recommended ? 'text-[#C6FF00]' : 'text-white'
                      }`} />
                      <span className="text-white/80 text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  className={`w-full py-3 rounded-full font-bold transition-all duration-300 uppercase tracking-widest text-xs ${
                    plan.recommended
                      ? 'bg-[#C6FF00] text-black hover:scale-105'
                      : 'glass hover:bg-white/10'
                  }`}
                  data-testid={`plan-cta-${index}`}
                >
                  Comenzar Ahora
                </button>
              </motion.div>
            ))}
          </div>
        )}
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center glass rounded-2xl p-8 max-w-3xl mx-auto"
        >
          <h3 className="text-2xl font-bold mb-4">¿Necesitas un plan personalizado?</h3>
          <p className="text-white/60 mb-6">
            Contacta con nuestro equipo para crear una solución a medida que se adapte perfectamente a tus necesidades.
          </p>
          <a
            href="mailto:contact@buildbrains.com"
            className="inline-block px-8 py-3 rounded-full glass hover:bg-white/10 transition-all duration-300 font-medium"
            data-testid="custom-plan-contact"
          >
            Contactar con Ventas
          </a>
        </motion.div>
      </div>
    </div>
  );
};